import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../service/auth.service';

type Status = 'loading' | 'pending' | 'approved' | 'denied' | 'expired' | 'invalid' | 'error';

export const MfaApprovePage = () => {
  const { t } = useTranslation();
  const { challengeId } = useParams<{ challengeId: string }>();
  const [status, setStatus] = React.useState<Status>('loading');
  const [responding, setResponding] = React.useState(false);

  React.useEffect(() => {
    if (!challengeId) {
      setStatus('invalid');
      return;
    }
    authService.mfaPushStatus(challengeId).then((res) => {
      setStatus(res.status as Status);
    }).catch(() => {
      setStatus('invalid');
    });
  }, [challengeId]);

  const handleRespond = async (action: 'approve' | 'deny') => {
    if (!challengeId || responding) return;
    setResponding(true);
    try {
      await authService.mfaPushRespond(challengeId, action);
      setStatus(action === 'approve' ? 'approved' : 'denied');
    } catch (err: any) {
      const msg = err.response?.data?.message || '';
      if (msg.includes('expired')) setStatus('expired');
      else if (msg.includes('already')) setStatus('denied');
      else setStatus('error');
    } finally {
      setResponding(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="bg-white dark:bg-[#1C1C1F] rounded-[24px] border border-[#E5E5ED] dark:border-[#2E2E30] shadow-[0px_4px_10px_rgba(42,60,68,0.08)] dark:shadow-[0px_4px_10px_rgba(0,0,0,0.3)] p-[32px] max-w-[400px] w-full mx-[16px] text-center">
        {status === 'loading' && (
          <p className="font-body text-[14px] text-[#818185]">{t('common.loading')}</p>
        )}

        {status === 'pending' && (
          <>
            <div className="w-[48px] h-[48px] mx-auto mb-[16px] rounded-full bg-[#F0F9FF] dark:bg-[#0ea5c9]/10 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[8px]">
              {t('auth.mfaPushApproveTitle')}
            </h2>
            <p className="font-body text-[14px] text-[#818185] mb-[24px]">
              {t('auth.mfaPushApproveBody')}
            </p>
            <div className="flex flex-col gap-[10px]">
              <button
                type="button"
                onClick={() => handleRespond('approve')}
                disabled={responding}
                className={`h-[44px] w-full rounded-[32px] font-body text-[14px] font-medium transition-all ${
                  responding ? 'bg-gray-900 text-white opacity-50' : 'bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB]'
                }`}
              >
                {responding ? t('common.loading') : t('auth.mfaPushApprove')}
              </button>
              <button
                type="button"
                onClick={() => handleRespond('deny')}
                disabled={responding}
                className={`h-[44px] w-full rounded-[32px] border border-red-500 text-red-500 font-body text-[14px] font-medium transition-all ${
                  responding ? 'opacity-50' : 'hover:bg-red-500 hover:text-white'
                }`}
              >
                {t('auth.mfaPushDeny')}
              </button>
            </div>
          </>
        )}

        {status === 'approved' && (
          <>
            <div className="w-[48px] h-[48px] mx-auto mb-[16px] rounded-full bg-[#ECFDF5] flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[8px]">
              {t('auth.mfaPushApproved')}
            </h2>
            <p className="font-body text-[14px] text-[#818185]">
              {t('auth.mfaPushApprovedDetail')}
            </p>
          </>
        )}

        {status === 'denied' && (
          <>
            <div className="w-[48px] h-[48px] mx-auto mb-[16px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D8210C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </div>
            <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[8px]">
              {t('auth.mfaPushDeniedConfirm')}
            </h2>
            <p className="font-body text-[14px] text-[#818185]">
              {t('auth.mfaPushDeniedDetail')}
            </p>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-[48px] h-[48px] mx-auto mb-[16px] rounded-full bg-[#FEF3C7] flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
            </div>
            <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[8px]">
              {t('auth.mfaPushExpired')}
            </h2>
            <p className="font-body text-[14px] text-[#818185]">
              {t('auth.mfaPushExpiredDetail')}
            </p>
          </>
        )}

        {(status === 'invalid' || status === 'error') && (
          <>
            <div className="w-[48px] h-[48px] mx-auto mb-[16px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D8210C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
            </div>
            <h2 className="font-body text-[18px] font-semibold text-base-black dark:text-white mb-[8px]">
              {t('auth.mfaPushInvalid')}
            </h2>
            <p className="font-body text-[14px] text-[#818185]">
              {t('auth.mfaPushInvalidDetail')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
